import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { Event } from "../types/event";
import { User } from "../types/user";
import { useAuth } from "./AuthContext";
import { useUsers } from "./UserContext";

interface EventsState {
  events: Event[];
  isLoadingEvents: boolean;
  error: string | null;
  refreshEvents: () => Promise<void>;
  addEvent: (event: Event) => void;
  updateEvent: (event: Event) => void;
  deleteEvent: (eventId: string) => void;
  updateUserEvents: (userId: string, userEvents: Event[]) => void;
}

const EventsContext = createContext<EventsState>({
  events: [],
  isLoadingEvents: true,
  error: null,
  refreshEvents: async () => {},
  addEvent: () => {},
  updateEvent: () => {},
  deleteEvent: () => {},
  updateUserEvents: () => {},
});

export function EventsProvider({ children }: { children: React.ReactNode }) {
  const { token } = useAuth();
  const { currentUser, colleagues } = useUsers();
  const [events, setEvents] = useState<Event[]>([]);
  const [isLoadingEvents, setIsLoadingEvents] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Function to fetch events for all users in the site
  const fetchAllSiteEvents = useCallback(async (users: User[], token: string) => {
    const allEvents: Event[] = [];

    // Fetch events for each user in the site
    for (const user of users) {
      try {
        const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3000/api'}/events`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'X-User-Id': user.id,
          },
        });

        if (response.ok) {
          const userEvents = await response.json();
          allEvents.push(...userEvents);
        }
      } catch (error) {
        console.warn(`Failed to fetch events for user ${user.id}:`, error);
      }
    }

    return allEvents;
  }, []);

  // Load events
  const loadEvents = useCallback(async () => {
    if (!token || !currentUser) {
      setIsLoadingEvents(false);
      return;
    }

    setIsLoadingEvents(true);
    setError(null);

    try {
      // For all users (admin and regular), fetch events from all users in the site
      // Regular users will see events filtered by their colleague visibility settings
      const allUsers = [currentUser, ...colleagues];
      const siteUsers = allUsers.filter((u: User) => u.site === currentUser.site && u.role !== 'admin');
      const eventsData = await fetchAllSiteEvents(siteUsers, token);
      
      setEvents(eventsData);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to load events";
      setError(errorMessage);
      console.error(errorMessage);
    } finally {
      setIsLoadingEvents(false);
    }
  }, [token, currentUser, colleagues, fetchAllSiteEvents]);

  // Selective update: update events for a specific user
  const updateUserEvents = useCallback((userId: string, userEvents: Event[]) => {
    setEvents(prev => {
      // Remove old events for this user and add new ones
      const filtered = prev.filter(e => e.userId !== userId);
      return [...filtered, ...userEvents];
    });
  }, []);

  // Add single event
  const addEvent = useCallback((event: Event) => {
    setEvents(prev => [...prev, event]);
  }, []);

  // Update single event
  const updateEvent = useCallback((event: Event) => {
    setEvents(prev => prev.map(e => e.id === event.id ? event : e));
  }, []);

  // Delete single event
  const deleteEvent = useCallback((eventId: string) => {
    setEvents(prev => prev.filter(e => e.id !== eventId));
  }, []);

  // Public method to refresh events
  const refreshEvents = useCallback(async () => {
    await loadEvents();
  }, [loadEvents]);

  // Load events when dependencies change
  useEffect(() => {
    if (token && currentUser) {
      loadEvents();
    } else {
      setEvents([]);
      setIsLoadingEvents(false);
      setError(null);
    }
  }, [token, currentUser, colleagues, loadEvents]);

  return (
    <EventsContext.Provider
      value={{
        events,
        isLoadingEvents,
        error,
        refreshEvents,
        addEvent,
        updateEvent,
        deleteEvent,
        updateUserEvents,
      }}
    >
      {children}
    </EventsContext.Provider>
  );
}

export function useEvents() {
  const context = useContext(EventsContext);
  if (!context) {
    throw new Error("useEvents must be used within an EventsProvider");
  }
  return context;
}
